﻿import { adminStrings } from './admin-strings';
import { createCollectionSlug } from './admin-helpers';
import { createAdminSession, readAdminError } from './admin-session';
import { calculateJustifiedGridLayout } from '../../SharedFrontend/src/justified-grid-layout';

type AdminPhoto = {
    imageKey: string;
    thumbnailImageUrl: string;
    previewWidth: number;
    previewHeight: number;
};

type AdminCollection = {
    name: string;
    slug: string;
    images: AdminPhoto[];
    isPublic: boolean;
};

type AdminState = {
    defaultCollectionSlug: string | null;
    zoomMode: ZoomMode;
    collections: AdminCollection[];
};

type ZoomMode = 'zoomToPoi' | 'zoomToFit';

const createModal = requiredElement<HTMLElement>('create-modal');
const createCollectionButton = requiredElement<HTMLButtonElement>('create-collection-button');
const closeCreateModalButton = requiredElement<HTMLButtonElement>('close-create-modal');
const createForm = requiredElement<HTMLFormElement>('create-form');
const createName = requiredElement<HTMLInputElement>('create-name');
const createSlugInput = requiredElement<HTMLInputElement>('create-slug');
const collectionsSurface = requiredElement<HTMLElement>('collections');
const statusSurface = requiredElement<HTMLElement>('status');
const zoomModeSelect = requiredElement<HTMLSelectElement>('zoom-mode');
let slugWasEdited = false;
let currentAdminState: AdminState | null = null;
let lastCollectionsWidth = 0;
const collectionGapPixels = 16;
const collectionTargetRowHeight = 576;
const fallbackCollectionAspectRatio = 4 / 3;

function requiredElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (element == null) throw new Error(`Required element #${id} was not found.`);
    return element as T;
}

function setStatus(message: string, kind: 'status' | 'error' = 'status'): void {
    statusSurface.textContent = message;
    statusSurface.dataset.kind = kind;
}

function openCreateModal(): void {
    createModal.hidden = false;
    document.body.classList.add('modal-open');
    createName.focus();
}

function closeCreateModal(restoreFocus = true): void {
    if (createModal.hidden) return;
    createModal.hidden = true;
    document.body.classList.remove('modal-open');
    if (restoreFocus) createCollectionButton.focus();
}

const adminSession = createAdminSession({
    signedOutMessage: adminStrings.signedOut,
    loginFailedMessage: adminStrings.loginFailed,
    requestFailedMessage: adminStrings.requestFailed,
    setStatus,
    onAuthenticated: loadCollections,
    onSignedOut: () => {
        closeCreateModal(false);
        collectionsSurface.replaceChildren();
    }
});
const apiRequest = adminSession.request;
const readError = (response: Response) => readAdminError(response, adminStrings.requestFailed);

async function loadCollections(): Promise<void> {
    setStatus(adminStrings.loading);
    const response = await apiRequest('/api/admin/collections');
    if (!response.ok) throw new Error(await readError(response));
    const state = await response.json() as AdminState;
    renderCollections(state);
    setStatus(state.collections.length === 0 ? adminStrings.noCollections : '');
}

function renderCollections(state: AdminState): void {
    currentAdminState = state;
    zoomModeSelect.value = state.zoomMode;
    const usableWidth = collectionsSurface.clientWidth;
    if (usableWidth <= 0) return;
    lastCollectionsWidth = usableWidth;
    const aspectRatios = state.collections.map(collection => {
        const firstImage = collection.images[0];
        return firstImage == null || firstImage.previewWidth <= 0 || firstImage.previewHeight <= 0
            ? fallbackCollectionAspectRatio
            : firstImage.previewWidth / firstImage.previewHeight;
    });
    const layout = calculateJustifiedGridLayout({
        aspectRatios,
        usableWidth,
        gap: collectionGapPixels,
        targetRowHeight: Math.min(collectionTargetRowHeight, usableWidth * 0.75),
        minimumItemsPerRow: 1,
        maximumItemsPerRow: 4,
        minimumItemSize: 1
    });
    const cards = state.collections.map(collection => createCollectionCard(collection));
    const rows = layout.rows.map(rowLayout => {
        const row = document.createElement('div');
        row.className = 'justified-collection-row';
        for (let index = rowLayout.itemStartIndex; index <= rowLayout.itemEndIndex; index++) {
            const card = cards[index];
            const itemLayout = layout.items[index];
            card.style.width = `${itemLayout.width}px`;
            card.style.height = `${itemLayout.height}px`;
            row.append(card);
        }
        return row;
    });
    collectionsSurface.replaceChildren(...rows);
}

// The collection grid is display-only: it lists every collection and links to the detail page where the
// collection-level controls (rename, visibility, default, delete) now live.
function createCollectionCard(collection: AdminCollection): HTMLElement {
    const card = document.createElement('article');
    card.className = 'collection';
    const editorUrl = `/admin/collection/?collection=${encodeURIComponent(collection.slug)}`;

    const thumbnail = document.createElement('div');
    thumbnail.className = 'collection-thumbnail';
    const firstImage = collection.images[0];
    if (firstImage == null) {
        const empty = document.createElement('span');
        empty.textContent = adminStrings.noImages;
        thumbnail.append(empty);
    } else {
        const image = new Image();
        image.src = firstImage.thumbnailImageUrl;
        image.alt = adminStrings.collectionThumbnail(collection.name);
        image.loading = 'lazy';
        thumbnail.append(image);
    }

    const overlay = document.createElement('div');
    overlay.className = 'collection-overlay';
    const metadata = document.createElement('div');
    metadata.className = 'collection-metadata';
    const name = document.createElement('span');
    name.className = 'collection-name';
    name.textContent = collection.name;
    const slug = document.createElement('span');
    slug.className = 'collection-slug';
    slug.textContent = collection.slug;
    metadata.append(name, slug);

    const cardLink = document.createElement('a');
    cardLink.className = 'collection-card-link';
    cardLink.href = editorUrl;
    cardLink.setAttribute('aria-label', adminStrings.editNamedCollection(collection.name));
    overlay.append(metadata);
    card.append(thumbnail, cardLink, overlay);
    return card;
}

async function ensureSuccess(response: Response): Promise<void> {
    if (!response.ok) throw new Error(await readError(response));
}

async function runMutation(action: () => Promise<void>): Promise<boolean> {
    try {
        await action();
        await loadCollections();
        return true;
    } catch (error) {
        setStatus(error instanceof Error ? error.message : adminStrings.requestFailed, 'error');
        return false;
    }
}

createName.addEventListener('input', () => {
    if (!slugWasEdited) createSlugInput.value = createCollectionSlug(createName.value);
});
createCollectionButton.addEventListener('click', openCreateModal);
closeCreateModalButton.addEventListener('click', () => closeCreateModal());
createModal.addEventListener('click', event => {
    if (event.target === createModal) closeCreateModal();
});
createModal.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
        event.preventDefault();
        closeCreateModal();
        return;
    }
    if (event.key !== 'Tab') return;

    const focusableElements = Array.from(createModal.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)'));
    const firstElement = focusableElements[0];
    const lastElement = focusableElements.at(-1);
    if (firstElement == null || lastElement == null) return;
    if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
    }
});
createSlugInput.addEventListener('input', () => { slugWasEdited = true; });
createForm.addEventListener('submit', event => {
    event.preventDefault();
    void (async () => {
        const created = await runMutation(async () => {
            const response = await apiRequest('/api/admin/collections', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: createName.value, slug: createSlugInput.value })
            });
            await ensureSuccess(response);
        });
        if (!created) return;
        createForm.reset();
        slugWasEdited = false;
        closeCreateModal();
    })();
});
zoomModeSelect.addEventListener('change', () => {
    zoomModeSelect.disabled = true;
    void runMutation(async () => {
        const response = await apiRequest('/api/admin/site-config/zoom-mode', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zoomMode: zoomModeSelect.value })
        });
        await ensureSuccess(response);
    }).finally(() => { zoomModeSelect.disabled = false; });
});
new ResizeObserver(() => {
    if (currentAdminState != null && collectionsSurface.clientWidth !== lastCollectionsWidth) {
        renderCollections(currentAdminState);
    }
}).observe(collectionsSurface);

void adminSession.initialize()
    .catch(error => setStatus(error instanceof Error ? error.message : adminStrings.requestFailed, 'error'));
